import { CircularProgress } from '@material-ui/core';
import { PhotoCameraOutlined as CameraIcon, Person as PersonIcon } from '@material-ui/icons';
import React, { useRef, useState } from 'react';

export const BIO_MAX = 200;
export const PHOTO_MAX_BYTES = 5000000;

/** Why a chosen file can't be used as a profile photo, or null if it can. */
export const photoProblem = file => {
  if (!file) return 'Choose a photo to upload.';
  if (!/^image\//.test(file.type || '')) return 'That file is not an image. Choose a JPG or PNG.';
  if (file.size > PHOTO_MAX_BYTES) return 'That photo is over 5 MB. Choose a smaller one.';
  return null;
};

export const inputClass = invalid =>
  `w-full box-border h-12 px-4 rounded-xl border border-solid bg-white text-base text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 ${
    invalid
      ? 'border-red-600 focus:border-red-600 focus:ring-red-600/10'
      : 'border-slate-300 focus:border-slate-900 focus:ring-slate-900/10'
  }`;

const Avatar = ({ url, size }) =>
  url ? (
    <img src={url} alt="" className="block rounded-full object-cover" style={{ width: size, height: size }} />
  ) : (
    <span
      className="flex items-center justify-center rounded-full bg-slate-200 text-slate-400"
      style={{ width: size, height: size }}>
      <PersonIcon style={{ fontSize: size * 0.6 }} />
    </span>
  );

/**
 * Onboarding step 1. Name, photo and a short line about the guide, with a live
 * preview of how they appear to guests on their tipping page.
 */
const ProfileStep = ({ values, errors, onChange, avatarUrl, uploading, onUploadPhoto }) => {
  const fileRef = useRef(null);
  const [photoError, setPhotoError] = useState(null);

  const choosePhoto = e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const problem = photoProblem(file);
    setPhotoError(problem);
    if (!problem) onUploadPhoto(file);
  };

  const displayName = [values.firstName, values.lastName].filter(Boolean).join(' ') || 'Your name';

  return (
    <div className="grid gap-8">
      <div className="flex items-center gap-5">
        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          className="relative shrink-0 p-0 border-0 bg-transparent rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
          aria-label={avatarUrl ? 'Change photo' : 'Add a photo'}>
          <Avatar url={avatarUrl} size={88} />
          <span className="absolute bottom-0 right-0 flex items-center justify-center w-8 h-8 rounded-full bg-indigo-600 text-white ring-2 ring-white">
            {uploading ? <CircularProgress size={14} color="inherit" /> : <CameraIcon style={{ fontSize: 16 }} />}
          </span>
        </button>
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={uploading}
            className="h-10 px-4 rounded-xl border border-solid border-slate-300 bg-white text-sm font-semibold text-slate-900 cursor-pointer hover:border-slate-500 transition-colors disabled:cursor-default disabled:text-slate-400">
            {uploading ? 'Uploading…' : avatarUrl ? 'Change photo' : 'Add a photo'}
          </button>
          <p className="text-sm text-slate-500 mt-2 mb-0">A clear face photo. JPG or PNG, up to 5 MB.</p>
          {photoError && (
            <p className="text-sm text-red-700 mt-1 mb-0" role="alert">
              {photoError}
            </p>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={choosePhoto}
          data-testid="onboarding-photo-input"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="onboarding-first-name" className="block text-sm font-semibold text-slate-700 mb-2">
            First name
          </label>
          <input
            id="onboarding-first-name"
            autoComplete="given-name"
            value={values.firstName}
            onChange={e => onChange('firstName', e.target.value)}
            aria-invalid={Boolean(errors.firstName)}
            className={inputClass(errors.firstName)}
          />
          {errors.firstName && <p className="text-sm text-red-700 mt-1 mb-0">{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="onboarding-last-name" className="block text-sm font-semibold text-slate-700 mb-2">
            Last name
          </label>
          <input
            id="onboarding-last-name"
            autoComplete="family-name"
            value={values.lastName}
            onChange={e => onChange('lastName', e.target.value)}
            aria-invalid={Boolean(errors.lastName)}
            className={inputClass(errors.lastName)}
          />
          {errors.lastName && <p className="text-sm text-red-700 mt-1 mb-0">{errors.lastName}</p>}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <label htmlFor="onboarding-bio" className="block text-sm font-semibold text-slate-700">
            A line about you <span className="font-normal text-slate-500">(optional)</span>
          </label>
          <span className="text-xs text-slate-500 tabular-nums">
            {(values.description || '').length}/{BIO_MAX}
          </span>
        </div>
        <textarea
          id="onboarding-bio"
          rows={3}
          maxLength={BIO_MAX}
          value={values.description}
          onChange={e => onChange('description', e.target.value)}
          placeholder="Born and raised here, guiding the old town for six years. Thanks for joining me."
          className="w-full box-border px-4 py-3 rounded-xl border border-solid border-slate-300 bg-white text-base text-slate-900 placeholder-slate-500 leading-relaxed resize-none focus:outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
        />
      </div>

      <div>
        <p className="text-sm font-semibold text-slate-700 mt-0 mb-2">How guests see you</p>
        <div className="flex items-start gap-4 p-5 rounded-xl bg-white border border-solid border-slate-200" data-testid="onboarding-preview">
          <Avatar url={avatarUrl} size={56} />
          <div className="min-w-0">
            <p className="text-base font-bold text-slate-900 m-0 break-words">{displayName}</p>
            <p className="text-sm text-slate-600 leading-relaxed m-0 mt-2 break-words">
              {values.description || 'Your line about yourself appears here.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileStep;
